import { extractFromMultiName } from "@/src/app/Locations/utils";
import { Combobox } from "@/src/components/ui/combobox";
import { DatePicker } from "@/src/components/ui/date-picker";
import { FormField, FormItem, FormLabel, FormControl } from "@/src/components/ui/form";
import { TimeSelect } from "@/src/components/ui/time-select";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { OfferYachtForm } from "../offer-yacht.schema";
import { format, parseISO, parse } from "date-fns";
import { fetch } from "@/src/lib/fetch";
import { Location } from "@/src/app/Locations/model";
import { useFormContext } from "react-hook-form";
import { Pricing } from "./Pricing";
import { YachtPrice } from "./ChangeYachtDetailsModal";
import { Amenity, YachtDetails } from "@/src/app/Yachts/model";
import { Separator } from "@/src/components/ui/separator";
import { Checkbox } from "@/src/components/ui/checkbox";
import { logOfferYachtControlChange, OfferYachtLogContext } from "../../offerCreatorLogs";

export type DatesAndPricingProps = {
  yacht: YachtDetails;
  amenities: Amenity[];
  price: YachtPrice | null;
  offerCurrencyCode: string;
  isLoading: boolean;
  logContext: OfferYachtLogContext;
};
export function DatesAndPricing(props: DatesAndPricingProps) {
  const form = useFormContext();

  const startDate = form.watch("startDate");

  return (
    <>
      <div className="flex flex-col gap-4">
        <h4 className="text-xl font-semibold">Start/end dates & Location</h4>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-4">
            <FormField<OfferYachtForm, "startDate">
              name="startDate"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Start date</FormLabel>
                    <FormControl>
                      <DatePicker
                        disabledDays={{ before: new Date() }}
                        className="w-full"
                        onChange={(v) => {
                          const before = field.value;
                          if (v) {
                            const formattedDate = format(v, "yyyy-MM-dd");
                            field.onChange(v ? formattedDate : null);
                            logOfferYachtControlChange({
                              eventType: "offer_yacht.dates_locations_changed",
                              context: props.logContext,
                              field: "startDate",
                              before,
                              after: formattedDate,
                            });
                            return formatDateUTC(v);
                          } else {
                            field.onChange(null);
                            logOfferYachtControlChange({
                              eventType: "offer_yacht.dates_locations_changed",
                              context: props.logContext,
                              field: "startDate",
                              before,
                              after: null,
                            });
                            return v;
                          }
                        }}
                        value={field.value ? normalizeDate(field.value) : undefined}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField<OfferYachtForm, "startTime">
              name="startTime"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Start time</FormLabel>
                    <FormControl>
                      <TimeSelect
                        onChange={(value) => {
                          logOfferYachtControlChange({
                            eventType: "offer_yacht.dates_locations_changed",
                            context: props.logContext,
                            field: "startTime",
                            before: field.value,
                            after: value,
                          });
                          field.onChange(value);
                        }}
                        value={field.value}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField<OfferYachtForm, "startLocation">
              name="startLocation"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>Start location</FormLabel>
                    <FormControl>
                      <Combobox<Location>
                        className="w-full"
                        placeholder="Search for location"
                        loadOptions={(query) =>
                          fetch<{ data: Location[] }>(
                            `/api/locations/locations?pageSize=5${query ? `&filter=${query}` : ""}`,
                          ).then((r) => r.data)
                        }
                        value={(field.value?.id ? field.value : null) as any}
                        onChange={(v) => {
                          logOfferYachtControlChange({
                            eventType: "offer_yacht.dates_locations_changed",
                            context: props.logContext,
                            field: "startLocation",
                            before: field.value,
                            after: v,
                          });
                          field.onChange(v);
                        }}
                        renderOption={(r) => extractFromMultiName(r.names)}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField<OfferYachtForm, "endDate">
              name="endDate"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>End date</FormLabel>
                    <FormControl>
                      <DatePicker
                        fromDate={new Date(startDate)}
                        disabledDays={{ before: (startDate as any) || new Date() }}
                        className="w-full"
                        onChange={(v) => {
                          const before = field.value;
                          if (v) {
                            const formattedDate = format(v, "yyyy-MM-dd");
                            field.onChange(v ? formattedDate : null);
                            logOfferYachtControlChange({
                              eventType: "offer_yacht.dates_locations_changed",
                              context: props.logContext,
                              field: "endDate",
                              before,
                              after: formattedDate,
                            });
                            return formatDateUTC(v);
                          } else {
                            field.onChange(null);
                            logOfferYachtControlChange({
                              eventType: "offer_yacht.dates_locations_changed",
                              context: props.logContext,
                              field: "endDate",
                              before,
                              after: null,
                            });
                            return v;
                          }
                        }}
                        value={field.value ? formatInTimeZone(field.value) : undefined}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField<OfferYachtForm, "endTime">
              name="endTime"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>End time</FormLabel>
                    <FormControl>
                      <TimeSelect
                        onChange={(value) => {
                          logOfferYachtControlChange({
                            eventType: "offer_yacht.dates_locations_changed",
                            context: props.logContext,
                            field: "endTime",
                            before: field.value,
                            after: value,
                          });
                          field.onChange(value);
                        }}
                        value={field.value}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <FormField<OfferYachtForm, "endLocation">
              name="endLocation"
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>End location</FormLabel>
                    <FormControl>
                      <Combobox<Location>
                        className="w-full"
                        placeholder="Search for location"
                        loadOptions={(query) =>
                          fetch<{ data: Location[] }>(
                            `/api/locations/locations?pageSize=5${query ? `&filter=${query}` : ""}`,
                          ).then((r) => r.data)
                        }
                        value={(field.value?.id ? field.value : null) as any}
                        onChange={(v) => {
                          logOfferYachtControlChange({
                            eventType: "offer_yacht.dates_locations_changed",
                            context: props.logContext,
                            field: "endLocation",
                            before: field.value,
                            after: v,
                          });
                          field.onChange(v);
                        }}
                        renderOption={(r) => extractFromMultiName(r.names)}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
          </div>
        </div>
        
      </div>
      
      <Separator />
      <div>
          <FormField<OfferYachtForm, "ignoreAvailability">
            name="ignoreAvailability"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start gap-3">
                <FormControl>
                  <Checkbox
                    className="mt-1 h-5 w-5"
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      logOfferYachtControlChange({
                        eventType: "offer_yacht.dates_locations_changed",
                        context: props.logContext,
                        field: "ignoreAvailability",
                        before: field.value,
                        after: !!checked,
                      });
                      field.onChange(!!checked);
                    }}
                  />
                </FormControl>
                <div className="!mt-0">
                  <FormLabel className="text-base leading-tight">Ignore availability</FormLabel>
                  <p className="text-sm text-muted-foreground">
                    Enable this when you need to override the API availability for the selected period.
                  </p>
                </div>
              </FormItem>
            )}
          />
        </div>
        <Separator />
      <Pricing {...props} />
    </>
  );
}

const normalizeDate = (dateString: string): Date => {
  const parsedDate = parseISO(dateString);
  if (isNaN(parsedDate.getTime())) {
    throw new Error("Invalid date");
  }
  return parsedDate;
};

const formatInTimeZone = (dateString: string) => {
  const parsedTime = parseISO(dateString);
  const formated = format(new Date(parsedTime), "yyyy-MM-dd");
  return parse(formated, "yyyy-MM-dd", new Date(parsedTime));
};
